import React, {
    FC,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';
import * as S from './Container.styled';
import { MAIN_BLOCK_ID, CONTAINER_BLOCK_ID } from '../../helpers/constants';
import { Logo, Sidebar } from 'components/Sidebar/Sidebar';
import { PopupProvider } from 'components/Popup';
import { UserContext } from 'context/User';
import {
    BigHeadline,
    CenterContainer,
    FlexedContainer,
    TextLNormal
} from 'ui/Styled/Styled';

import { ContainerMenuProps } from 'ui/Menu/Menu.types';
import { SidebarOverlay } from 'components/Sidebar/Sidebar.styled';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { enablePageScroll } from 'utils/scroll';
import { ButtonProps } from 'ui/Button';
import {
    ContainerContext,
    ContainerContextProps,
    useOpenSidebar
} from './Container.context';

export type ContainerProps = ChildrenProps & {
    disablePaddingTop?: boolean;
    checkEmailConfirm?: boolean;
    heading?: React.ReactNode;
    headingRightContent?: React.ReactNode;
    flex?: boolean;
    buttons?: ButtonProps[];
    hideHeading?: boolean;
} & ContainerMenuProps;

const PAYMENT_STATUS_GET_PARAM = 'payment_status';

export type HeadingProps = ChildrenProps & {
    rightContent?: React.ReactNode;
    wrap?: boolean;
};

export const SiteHeader: FC<HeadingProps> = ({
    children,
    rightContent,
    wrap
}) => {
    const open = useOpenSidebar();

    return (
        <S.Heading $disableMargin={!children && !rightContent} $wrap={wrap}>
            <FlexedContainer>
                <S.OpenMenuButton onClick={open} />
            </FlexedContainer>
            {children ? (
                <S.Headline>{children}</S.Headline>
            ) : (
                <S.LogoContainer>
                    <Logo />
                </S.LogoContainer>
            )}
            <S.HeadingRightContentContainer>
                {rightContent}
            </S.HeadingRightContentContainer>
        </S.Heading>
    );
};

const Container: FC<ContainerProps> = ({
    children,
    disablePaddingTop,
    checkEmailConfirm = true,
    heading,
    flex = false,
    hideHeading,
    headingRightContent,
    ...sidebarProps
}) => {
    const [params, setParams] = useSearchParams();

    const location = useLocation();

    useEffect(() => {
        enablePageScroll();
    }, [location]);

    useEffect(() => {
        const paymentStatus = params.get(PAYMENT_STATUS_GET_PARAM);

        if (paymentStatus) {
            switch (paymentStatus) {
                case 'succeeded':
                    toast.success('Баланс пополнен!');
                    setTimeout(() => {
                        params.delete(PAYMENT_STATUS_GET_PARAM);
                        setParams(params);
                    });
                    break;
                default:
                    toast.error(
                        <>
                            Произошла ошибка при оплате: неизвестный статус.
                            <br />
                            Обратитесь в поддержку.
                        </>
                    );
            }
        }
    }, [params, setParams]);

    const { user } = useContext(UserContext);
    const [isOpen, setIsOpen] = useState(false);

    const openSidebar = useCallback(() => {
        setIsOpen(true);
    }, []);

    const closeSidebar = useCallback(() => {
        setIsOpen(false);
    }, []);

    const providerValue = useMemo<ContainerContextProps>(
        () => ({ openSidebar, closeSidebar }),
        [openSidebar, closeSidebar]
    );

    return (
        <ContainerContext.Provider value={providerValue}>
            <S.Container id={CONTAINER_BLOCK_ID} $flex={flex}>
                {isOpen && <SidebarOverlay onClick={closeSidebar} />}
                <Sidebar {...sidebarProps} isOpen={isOpen} />

                <S.Wrapper id={MAIN_BLOCK_ID}>
                    <PopupProvider>
                        <S.MainContainer
                            $disablePaddingTop={disablePaddingTop}
                            $disableFlex={!flex}
                        >
                            {!hideHeading && (
                                <SiteHeader
                                    rightContent={headingRightContent}
                                    wrap
                                >
                                    {heading}
                                </SiteHeader>
                            )}
                            <S.Main>
                                {checkEmailConfirm &&
                                user?.isEmailConfirmed === false ? (
                                    <CenterContainer>
                                        <BigHeadline>
                                            Почта не подтверждена
                                        </BigHeadline>
                                        <TextLNormal>
                                            На вашу почту <b>{user.email}</b>{' '}
                                            было отправлено письмо с
                                            подтверждением.
                                            <br />
                                            Пройдите по ссылке из письма для
                                            продолжения работы с сервисом.
                                        </TextLNormal>
                                    </CenterContainer>
                                ) : (
                                    <>{children}</>
                                )}
                            </S.Main>
                        </S.MainContainer>
                    </PopupProvider>
                </S.Wrapper>
            </S.Container>
        </ContainerContext.Provider>
    );
};

export default Container;
